import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../i18n';

const getToken = () => sessionStorage.getItem('auth_token');

const formatDate = (iso) => {
  if (!iso) return 'No expiry';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const isExpired = (iso) => iso && new Date(iso) < new Date();

const AdminTab = () => {
  const { t } = useI18n();
  const [logins, setLogins] = useState([]);
  const [loadError, setLoadError] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [expiry, setExpiry] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState(false);

  const loadLogins = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await fetch('/.netlify/functions/auth-manage', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLogins(data.passwords);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { loadLogins(); }, [loadLogins]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!username.trim()) { setCreateError(t('admin_create_error_username_required')); return; }
    if (!password.trim()) { setCreateError(t('admin_create_error_password_required')); return; }
    setCreating(true);
    setCreateError('');
    setCreateSuccess(false);
    try {
      const res = await fetch('/.netlify/functions/auth-manage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          label: '',
          username: username.trim(),
          password: password.trim(),
          expiresAt: expiry ? new Date(expiry).toISOString() : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateError(`Error ${res.status}: ${data.error || JSON.stringify(data)}`);
      } else {
        setLogins((prev) => [...prev, data.password]);
        setUsername('');
        setPassword('');
        setExpiry('');
        setCreateSuccess(true);
        setTimeout(() => setCreateSuccess(false), 3000);
      }
    } catch {
      setCreateError(t('admin_create_error_connection'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, uname) => {
    const confirmMsg = t('admin_delete_confirm').replace('{uname}', uname);
    if (!window.confirm(confirmMsg)) return;
    try {
      await fetch('/.netlify/functions/auth-manage', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ id }),
      });
      setLogins((prev) => prev.filter((l) => l.id !== id));
    } catch {
      alert(t('admin_delete_error'));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-inner">

        <div className="admin-header">
          <h2 className="admin-title">{t('admin_title')}</h2>
          <p className="admin-desc"></p>
        </div>

        {/* Create form */}
        <div className="admin-card">
          <h3 className="admin-card-title">{t('admin_add_card_title')}</h3>
          <form onSubmit={handleCreate} className="admin-create-form">
            <div className="admin-field">
              <label className="admin-field-label">{t('admin_username_label')}</label>
              <input
                type="text"
                className="admin-field-input"
                placeholder={t('admin_username_placeholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                maxLength={100}
                disabled={creating}
                autoComplete="off"
              />
            </div>
            <div className="admin-field">
              <label className="admin-field-label">{t('admin_password_label')}</label>
              <input
                type="text"
                className="admin-field-input"
                placeholder={t('admin_password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={100}
                disabled={creating}
                autoComplete="off"
              />
            </div>
            <div className="admin-field">
              <label className="admin-field-label">{t('admin_expires_label')}</label>
              <input
                type="date"
                className="admin-field-input"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                disabled={creating}
              />
            </div>
            <div className="admin-field admin-field--action">
              <label className="admin-field-label">&nbsp;</label>
              <button type="submit" className="admin-add-btn" disabled={creating}>
                {creating ? t('admin_adding') : t('admin_add_btn')}
              </button>
            </div>
          </form>
          {createError && <p className="admin-msg admin-msg--error">{createError}</p>}
          {createSuccess && <p className="admin-msg admin-msg--success">{t('admin_create_success')}</p>}
        </div>

        {/* Logins table */}
        <div className="admin-card">
          <h3 className="admin-card-title">{t('admin_card_active_logins')}</h3>

          {loadError && (
            <div className="admin-empty">
              <p>{t('admin_load_error')}</p>
              <button className="admin-refresh-btn" onClick={loadLogins}>{t('admin_try_again')}</button>
            </div>
          )}

          {!loadError && logins.length === 0 && (
            <div className="admin-empty">
              <p>{t('admin_no_logins')}</p>
            </div>
          )}

          {!loadError && logins.length > 0 && (
            <table className="admin-tbl">
              <thead>
                <tr>
                  <th>{t('admin_table_username')}</th>
                  <th>{t('admin_table_password')}</th>
                  <th>{t('admin_table_expires')}</th>
                  <th>{t('admin_table_status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logins.map((l) => (
                  <tr key={l.id} className={isExpired(l.expiresAt) ? 'admin-row--expired' : ''}>
                    <td className="admin-mono">{l.username}</td>
                    <td className="admin-mono">{l.password}</td>
                    <td>{l.expiresAt ? formatDate(l.expiresAt) : t('admin_no_expiry')}</td>
                    <td>
                      <span className={`admin-badge ${isExpired(l.expiresAt) ? 'admin-badge--expired' : 'admin-badge--active'}`}>
                        {isExpired(l.expiresAt) ? t('admin_status_expired') : t('admin_status_active')}
                      </span>
                    </td>
                    <td>
                      <button
                        className="admin-del-btn"
                        onClick={() => handleDelete(l.id, l.username)}
                      >
                        {t('admin_table_remove')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminTab;
